import { FieldPolicy } from '@apollo/client'

interface PaginatedResponse<T> {
  entities: T[]
  totalCount?: number
  hasNextPage?: boolean
  cursor?: string | null
  __typename?: string
}

export function nestedOffsetLimitPagination<T = any>(): FieldPolicy<PaginatedResponse<T>> {
  return {
    keyArgs(args) {
      if (!args) return false
      const payload = args.request || args.input || args
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { skip, limit, ...rest } = payload
      const sortedRest = Object.keys(rest)
        .sort()
        .reduce((acc, key) => {
          acc[key] = rest[key as keyof typeof rest]
          return acc
        }, {} as Record<string, any>)
      return JSON.stringify(sortedRest)
    },
    read(existing, { args }) {
      if (!existing || !existing.entities) return undefined
      const payload = args?.request || args?.input || args
      const skip = payload?.skip ?? 0
      const limit = payload?.limit ?? 20
      const collection = existing.entities.slice(skip, skip + limit)
      if (collection.length === 0 && existing.totalCount && existing.totalCount > skip) return undefined
      return {
        ...existing,
        entities: collection,
        hasNextPage: existing.totalCount !== undefined ? skip + limit < existing.totalCount : existing.hasNextPage,
      }
    },
    merge(existing, incoming, { args }) {
      const payload = args?.request || args?.input || args
      const skip = payload?.skip ?? 0
      const limit = payload?.limit ?? 20
      const existingEntities = existing?.entities ?? []
      const incomingEntities = incoming?.entities ?? []
      const merged = existingEntities.slice(0)
      for (let i = 0; i < incomingEntities.length; ++i) {
        merged[skip + i] = incomingEntities[i]
      }
      // A short page is the last one, so drop cached entries past it (e.g. a list that shrank after a mutation)
      if (incomingEntities.length < limit) merged.length = skip + incomingEntities.length
      if (incoming?.totalCount !== undefined && merged.length > incoming.totalCount) {
        merged.length = incoming.totalCount
      }
      return {
        ...incoming,
        entities: merged,
        totalCount: incoming.totalCount !== undefined ? incoming.totalCount : existing?.totalCount,
      }
    },
  }
}
